import { renderBatchEditCampaignTitle } from 'components/BatchEditCampaign/BatchEditCampaignTitle';
import { ModalState } from 'containers/Common/ModalStateFactory';
import i18n from 'i18n';
import { ModifyScheduleStateContent } from './ModifyScheduleStateContent';
import { useModifyScheduleStateContentModel } from './ModifyScheduleStateContentModel';
import { ScheduleConfig, ScheduleData } from 'core/rtbCampaign/ScheduleData';
import { ScheduleSummaryStateContent } from './ScheduleSummaryStateContent';
import { useScheduleSummaryStateContentModel } from './ScheduleSummaryStateContentModel';
import {
  DefaultRtbCampaignManager,
  RtbCampaignManager
} from 'core/rtbCampaign/RtbCampaignManager';
import moment from 'moment';
import { toast } from 'react-toastify';

abstract class BasicScheduleManagementState {
  back?: () => void;
  next?: () => void;
  close?: (dirty: boolean, beforeClose?: () => Promise<void>) => void;

  constructor(
    protected campaigns: {
      id: number;
      name: string;
      startDate: string;
      endDate: string;
    }[],
    protected scheduleConfig: ScheduleConfig,
    protected getContextData: () => ScheduleData | undefined
  ) {}

  get renderTitle() {
    return () => renderBatchEditCampaignTitle(this.campaigns);
  }

  setBackFn = (back: () => void) => {
    this.back = back;
  };

  setNextFn = (next: () => void) => {
    this.next = next;
  };

  setCloseFn = (close: (dirty: boolean) => void) => {
    this.close = close;
  };

  canGoNext = () => {
    return true;
  };
}

export class ModifyScheduleState
  extends BasicScheduleManagementState
  implements ModalState
{
  constructor(
    campaigns: {
      id: number;
      name: string;
      startDate: string;
      endDate: string;
    }[],
    scheduleConfig: ScheduleConfig,
    getContextData: () => ScheduleData | undefined
  ) {
    super(campaigns, scheduleConfig, getContextData);
    this.next = async () => {
      this.close && this.close(true);
    };
  }

  get content() {
    return ModifyScheduleStateContent;
  }

  get contentModel() {
    return () =>
      useModifyScheduleStateContentModel(
        this.scheduleConfig,
        this.validateDateRange
      );
  }

  get btnData() {
    const closeFn = this.close;
    return {
      primaryButton: this.next && {
        title: i18n.t('common.buttons.goSummary'),
        callback: this.next
      },
      secondaryButton: this.back && {
        title: i18n.t('common.buttons.backModify'),
        callback: this.back
      },
      dangerButton: closeFn && {
        title: i18n.t('common.buttons.cancel'),
        callback: () => {
          closeFn(false);
        },
        variant: 'secondary'
      }
    };
  }

  validateDateRange = (startDate: string, endDate: string) => {
    let start = new Date(startDate);
    let end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 92) {
      return i18n.t('campaign.descriptions.moreThanDays', { days: 92 });
    }
  };

  canGoNext = () => {
    const scheduleData: ScheduleData | undefined = this.getContextData();
    if (!scheduleData) {
      return false;
    }
    // Ensure that start date is before end date if both are enabled
    if (scheduleData.enableStartDate && scheduleData.enableEndDate) {
      const startDate = moment(scheduleData.startDate);
      const endDate = moment(scheduleData.endDate);
      if (startDate.isAfter(endDate)) {
        return false;
      }
    }
    // Ensure that one of the dates is enabled
    if (!scheduleData.enableStartDate && !scheduleData.enableEndDate) {
      return false;
    }
    // If only start date is enabled, it must be a valid date
    if (scheduleData.enableStartDate && !scheduleData.startDate) {
      return false;
    }
    // If only end date is enabled, it must be a valid date
    if (scheduleData.enableEndDate && !scheduleData.endDate) {
      return false;
    }
    if (
      this.validateDateRange(scheduleData.startDate, scheduleData.endDate) !==
      undefined
    ) {
      return false;
    }
    return true;
  };
}

export class ScheduleSummaryState
  extends BasicScheduleManagementState
  implements ModalState
{
  constructor(
    campaigns: {
      id: number;
      name: string;
      startDate: string;
      endDate: string;
    }[],
    scheduleConfig: ScheduleConfig,
    getContextData: () => ScheduleData | undefined,
    private campaignManager: RtbCampaignManager = new DefaultRtbCampaignManager()
  ) {
    super(campaigns, scheduleConfig, getContextData);
    this.next = async () => {
      const scheduleData: ScheduleData | undefined = this.getContextData();
      if (!scheduleData) {
        throw new Error('Schedule data is not initialized');
      }
      this.close &&
        this.close(true, () =>
          this.campaignManager
            .updateScheduleData(
              this.campaigns
                .filter((campaign) => !this.conflictIds.includes(campaign.id))
                .map((campaign) => campaign.id),
              scheduleData
            )
            .then(() => {
              toast.success(
                i18n.t('modifyScheduleState.messages.scheduleUpdated')
              );
            })
        );
    };
  }

  get content() {
    return ScheduleSummaryStateContent;
  }

  get contentModel() {
    return () =>
      useScheduleSummaryStateContentModel(
        this.scheduleConfig,
        this.conflictIds
      );
  }

  get btnData() {
    const closeFn = this.close;
    return {
      primaryButton: this.next && {
        title: i18n.t('common.buttons.ok'),
        callback: this.next
      },
      secondaryButton: this.back && {
        title: i18n.t('common.buttons.backModify'),
        callback: this.back
      },
      dangerButton: closeFn && {
        title: i18n.t('common.buttons.cancel'),
        callback: () => {
          closeFn(false);
        },
        variant: 'secondary'
      }
    };
  }

  private get conflictIds(): number[] {
    const scheduleData: ScheduleData | undefined = this.getContextData();
    if (!scheduleData) {
      return [];
    }
    return this.scheduleConfig.campaigns
      .filter((campaign) => {
        const campaignStartDate = moment(campaign.startDate);
        const campaignEndDate = moment(campaign.endDate);
        const isStartDateConflict =
          scheduleData.enableStartDate &&
          !scheduleData.enableEndDate &&
          campaignEndDate.isBefore(moment(scheduleData.startDate));
        const isEndDateConflict =
          scheduleData.enableEndDate &&
          !scheduleData.enableStartDate &&
          campaignStartDate.isAfter(moment(scheduleData.endDate));
        return isStartDateConflict || isEndDateConflict;
      })
      .map((campaign) => campaign.id);
  }
}
